import { Dispatch, SetStateAction, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Switch } from "@headlessui/react";
import type { AxiosError } from "axios";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  Hash,
  Mail,
  Percent,
  Plus,
  TicketPercent,
  Trash2,
  UserRound,
  UsersRound,
} from "lucide-react";
import Selectbox, { option } from "../../components/Shared/SelectBox";
import Input from "../../components/Shared/Input";
import Button from "../../components/Shared/Button";
import { queryClient } from "../../main";
import DeleteModal from "../../components/Shared/DeleteModal";
import Spinner from "../../components/Shared/Spinner";
import { CreateCoupon } from "../../api/Coupon/AddCoupon";
import { GetCoupons } from "../../api/Coupon/GetAllCoupons";
import { DeleteCoupon } from "../../api/Coupon/DeleteCoupon";
import { UpdateCouponStatus } from "../../api/Coupon/UpdateCouponStatus";
import { useUserDataStore } from "../../stores/user";
import { ButtonTypes } from "../../utils/consts";

const validationSchema = Yup.object({
  name: Yup.string().required("Title is required"),
  discount: Yup.string().required("Discount percentage is required"),
});

type CouponOption = option & {
  isActive: boolean;
  students: {
    id: string;
    email: string;
    fullName: string;
    phone: string;
  }[];
};

const Coupons = () => {
  const [selectedCoupon, setSelectedCoupon] = useState<CouponOption | null>(
    null
  );
  const [isOpen, setIsOpen] = useState(false);
  const { userData } = useUserDataStore();
  const userRole = userData?.role;

  const { mutate } = useMutation({
    mutationFn: CreateCoupon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coupon"] });
      toast.success("Coupon Added");
      formik.setSubmitting(false);
      formik.resetForm();
    },
    onError: () => {
      formik.setSubmitting(false);
    },
  });

  const { data: CouponResponse, isLoading: isCouponLoading } = useQuery({
    queryFn: GetCoupons,
    queryKey: ["coupon"],
    staleTime: 0,
    refetchInterval: 0,
  });

  const { mutate: updateCouponStatus, isPending: isStatusUpdating } =
    useMutation({
      mutationFn: UpdateCouponStatus,
      onSuccess: (response, variables) => {
        const isActive = response.data?.isActive ?? variables.isActive;

        setSelectedCoupon((coupon) =>
          coupon?.id === variables.id ? { ...coupon, isActive } : coupon
        );
        queryClient.invalidateQueries({ queryKey: ["coupon"] });
        toast.success(response.message || "Coupon status updated successfully");
      },
      onError: (error) => {
        const apiError = error as AxiosError<{ message?: string }>;
        toast.error(
          apiError.response?.data?.message || "Failed to update coupon status"
        );
      },
    });

  const coupons = CouponResponse?.data?.CouponData || [];
  const couponOptions: CouponOption[] = coupons.map((coupon) => ({
    id: coupon.id,
    name: coupon.code,
    code: coupon.code,
    discount: coupon.discount,
    count: coupon.count,
    isActive: coupon.isActive,
    students: coupon.students || [],
  }));

  const totalRedemptions = couponOptions.reduce(
    (sum, coupon) => sum + Number(coupon.count || 0),
    0
  );

  const formik = useFormik({
    initialValues: {
      name: "",
      code: "",
      discount: "",
    },
    validationSchema,
    onSubmit: (values) => {
      mutate({
        ...values,
        discount: Number(values.discount),
      });
    },
  });

  const handleDelete = async (id: string) => {
    await DeleteCoupon(id);
    setSelectedCoupon(null);
    toast.success("Coupon deleted successfully");
  };

  const handleCouponSelection: Dispatch<SetStateAction<option | null>> = (
    value
  ) => {
    const next =
      typeof value === "function" ? value(selectedCoupon) : value;

    if (!next) {
      setSelectedCoupon(null);
      return;
    }

    const coupon = next as CouponOption;
    setSelectedCoupon({
      ...coupon,
      students: coupon.students || [],
    });
  };

  if (isCouponLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6 py-7">
      <DeleteModal
        queryKey="coupon"
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        mutationFn={handleDelete}
        itemId={selectedCoupon?.id || ""}
        itemName={selectedCoupon?.code?.toString()}
      />

      <section className="overflow-hidden rounded-3xl border border-black/10 bg-white shadow">
        <div className="flex flex-col gap-5 border-b border-black/10 bg-primary px-6 py-6 text-white xl:flex-row xl:items-center xl:justify-between">
          <div>
            <p className="text-xs font-bold uppercase text-white/70">
              Discounts and redemptions
            </p>
            <h1 className="mt-1 text-3xl font-bold">Coupons</h1>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center xl:min-w-[480px]">
            <div className="rounded-2xl bg-white/10 px-4 py-3">
              <p className="text-2xl font-bold">{couponOptions.length}</p>
              <p className="text-xs font-semibold text-white/70">Coupons</p>
            </div>
            <div className="rounded-2xl bg-white/10 px-4 py-3">
              <p className="text-2xl font-bold">{totalRedemptions}</p>
              <p className="text-xs font-semibold text-white/70">Uses</p>
            </div>
            <div className="rounded-2xl bg-white/10 px-4 py-3">
              <p className="text-2xl font-bold">
                {selectedCoupon?.students.length || 0}
              </p>
              <p className="text-xs font-semibold text-white/70">Selected</p>
            </div>
          </div>
        </div>

        <div className="grid gap-6 p-5 xl:grid-cols-[minmax(0,1fr)_420px] xl:p-6">
          <section className="rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
            <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-end">
              <div className="min-w-0 flex-1">
                <div className="mb-4 flex items-center gap-3">
                  <span className="grid size-11 place-items-center rounded-2xl bg-primary text-white">
                    <TicketPercent size={20} />
                  </span>
                  <div>
                    <h2 className="text-xl font-bold text-textPrimary">
                      Existing Coupons
                    </h2>
                    <p className="text-sm font-medium text-lightGray">
                      Select a coupon to inspect usage
                    </p>
                  </div>
                </div>
                <Selectbox
                  title="Select Coupon"
                  selectOptions={couponOptions}
                  placeholder="Choose a Coupon"
                  selected={selectedCoupon}
                  setSelected={handleCouponSelection}
                  searchable
                  searchPlaceholder="Search coupons..."
                />
              </div>
              {selectedCoupon && (
                <div className="flex flex-wrap items-center gap-3">
                  {userRole === "admin" && (
                    <div className="flex h-12 items-center gap-3 rounded-2xl border border-black/10 bg-dashboardBg px-4">
                      <span
                        className={
                          selectedCoupon.isActive
                            ? "text-sm font-bold text-green-600"
                            : "text-sm font-bold text-lightGray"
                        }
                      >
                        {selectedCoupon.isActive ? "Active" : "Inactive"}
                      </span>
                      <Switch
                        checked={selectedCoupon.isActive}
                        onChange={(isActive) =>
                          updateCouponStatus({
                            id: selectedCoupon.id || "",
                            isActive,
                          })
                        }
                        disabled={isStatusUpdating}
                        aria-label="Toggle coupon activity"
                        className={
                          "relative inline-flex h-7 w-12 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary/50 disabled:cursor-not-allowed disabled:opacity-60 " +
                          (selectedCoupon.isActive
                            ? "bg-green-500"
                            : "bg-gray-300")
                        }
                      >
                        <span
                          aria-hidden="true"
                          className={
                            "pointer-events-none inline-block size-6 rounded-full bg-white shadow transition duration-200 " +
                            (selectedCoupon.isActive
                              ? "translate-x-5"
                              : "translate-x-0")
                          }
                        />
                      </Switch>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setIsOpen(true)}
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl border border-red-100 bg-red-50 px-4 text-sm font-bold text-red-600 transition hover:bg-red-100"
                  >
                    <Trash2 size={17} />
                    Delete
                  </button>
                </div>
              )}
            </div>

            {selectedCoupon ? (
              <div className="space-y-5">
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl bg-dashboardBg p-4">
                    <span className="mb-3 grid size-10 place-items-center rounded-xl bg-primary/10 text-primary">
                      <Hash size={18} />
                    </span>
                    <p className="text-xs font-bold uppercase text-lightGray">
                      Code
                    </p>
                    <p className="mt-1 truncate text-lg font-bold text-textPrimary">
                      {selectedCoupon.code}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-dashboardBg p-4">
                    <span className="mb-3 grid size-10 place-items-center rounded-xl bg-secondary/10 text-secondary">
                      <Percent size={18} />
                    </span>
                    <p className="text-xs font-bold uppercase text-lightGray">
                      Discount
                    </p>
                    <p className="mt-1 text-lg font-bold text-textPrimary">
                      {selectedCoupon.discount}%
                    </p>
                  </div>
                  <div className="rounded-2xl bg-dashboardBg p-4">
                    <span className="mb-3 grid size-10 place-items-center rounded-xl bg-primary/10 text-primary">
                      <UsersRound size={18} />
                    </span>
                    <p className="text-xs font-bold uppercase text-lightGray">
                      Count Used
                    </p>
                    <p className="mt-1 text-lg font-bold text-textPrimary">
                      {selectedCoupon.count || 0}
                    </p>
                  </div>
                </div>

                <div>
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h3 className="text-lg font-bold text-textPrimary">
                      Redeemed Students
                    </h3>
                    <span className="rounded-full bg-secondary/10 px-3 py-1 text-xs font-bold text-primary">
                      {selectedCoupon.students.length} students
                    </span>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    {selectedCoupon.students.length ? (
                      selectedCoupon.students.map((student) => (
                        <article
                          key={student.id}
                          className="rounded-2xl border border-black/10 bg-white p-4 shadow-sm"
                        >
                          <div className="mb-3 flex items-center gap-3">
                            <span className="grid size-11 place-items-center rounded-2xl bg-primary text-white">
                              <UserRound size={18} />
                            </span>
                            <div className="min-w-0">
                              <p className="truncate font-bold text-textPrimary">
                                {student.fullName}
                              </p>
                              <p className="truncate text-xs font-semibold text-lightGray">
                                {student.phone || "N/A"}
                              </p>
                            </div>
                          </div>
                          <p className="flex min-w-0 items-center gap-2 truncate text-sm font-medium text-lightGray">
                            <Mail size={15} />
                            <span className="truncate">{student.email}</span>
                          </p>
                        </article>
                      ))
                    ) : (
                      <div className="col-span-full rounded-2xl border border-dashed border-black/15 bg-dashboardBg p-8 text-center">
                        <p className="font-bold text-textPrimary">
                          No redemptions yet
                        </p>
                        <p className="mt-1 text-sm font-medium text-lightGray">
                          Student usage will appear here.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-black/15 bg-dashboardBg p-10 text-center">
                <p className="text-lg font-bold text-textPrimary">
                  Choose a coupon
                </p>
                <p className="mt-1 text-sm font-medium text-lightGray">
                  The coupon details and student usage will appear here.
                </p>
              </div>
            )}
          </section>

          {userRole === "admin" && (
            <aside className="rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
              <div className="mb-5 flex items-center gap-3">
                <span className="grid size-11 place-items-center rounded-2xl bg-secondary/10 text-secondary">
                  <Plus size={20} />
                </span>
                <div>
                  <h2 className="text-xl font-bold text-textPrimary">
                    Add New Coupon
                  </h2>
                  <p className="text-sm font-medium text-lightGray">
                    Create a new discount code
                  </p>
                </div>
              </div>

              <form onSubmit={formik.handleSubmit} className="space-y-4">
                <div className="grid gap-4">
                  <Input
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.name && formik.errors.name
                        ? formik.errors.name
                        : ""
                    }
                    name="name"
                    title="Title"
                    inputStyles="!h-12"
                  />
                  <Input
                    value={formik.values.code}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="code"
                    title="Code"
                    placeholder="ACCOUNTING15 (Optional)"
                    inputStyles="!h-12"
                  />
                  <Input
                    value={formik.values.discount}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    type="number"
                    error={
                      formik.touched.discount && formik.errors.discount
                        ? formik.errors.discount
                        : ""
                    }
                    name="discount"
                    title="Discount"
                    inputStyles="!h-12"
                  />
                </div>
                <Button
                  disabled={formik.isSubmitting}
                  className="!h-12 w-full !rounded-2xl !text-sm font-bold"
                  variant="primary"
                  type={ButtonTypes.SUBMIT}
                >
                  <Plus size={18} />
                  Add Coupon
                </Button>
              </form>
            </aside>
          )}
        </div>
      </section>
    </div>
  );
};

export default Coupons;
